import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, STUDENT_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/classes")({
  validateSearch: (search: Record<string, unknown>) => ({
    join: typeof search.join === "string" ? search.join : undefined,
  }),
  component: StudentClasses,
  head: () => ({
    meta: [
      { title: "My classes - Inquira" },
      { name: "description", content: "Join a class with a code and see every classroom you belong to on Inquira." },
      { property: "og:title", content: "My Inquira classes" },
      { property: "og:description", content: "Join with a class code and open your classrooms." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Membership {
  class_id: string;
  classes: { id: string; name: string; subject: string | null; grade_level: string | null } | null;
}

function StudentClasses() {
  const { join } = Route.useSearch();
  const { user } = useAuth();
  const [rows, setRows] = useState<Membership[]>([]);
  const [code, setCode] = useState(join ?? "");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("class_members")
      .select("class_id, classes(id, name, subject, grade_level)")
      .eq("student_id", user.id);
    setRows((data as unknown as Membership[]) ?? []);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  const joinClass = useCallback(async (value: string) => {
    if (!value.trim()) { toast.error(i18n.t("edu.classes.enter_your_class_code")); return; }
    setBusy(true);
    const { error } = await eduRpc<string>("edu_join_class", { _code: value.trim().toUpperCase() });
    setBusy(false);
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.classes.you_joined_the_class"));
    setCode("");
    void load();
  }, [load]);

  if (!user) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.classes.my_classes")}>
        <EmptyState title={i18n.t("edu.classes.sign_in_first")} body="Sign in to join a class and see your assignments." />
        <div className="mt-4">
          <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><T k="edu.classes.sign_in" /></Link>
        </div>
      </EduShell>
    );
  }

  return (
    <EduShell nav={STUDENT_NAV} title={i18n.t("edu.classes.my_classes")} subtitle={i18n.t("edu.classes.ask_your_teacher_for_the_class_code")}>
      <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-rule bg-card p-4">
        <input
          className="min-w-[180px] flex-1 rounded-xl border border-rule bg-background px-3 py-2 font-mono text-sm uppercase text-ink"
          placeholder={i18n.t("edu.classes.class_code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button onClick={() => joinClass(code)} disabled={busy}>{busy ? "Joining…" : "Join class"}</Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState title={i18n.t("edu.classes.no_classes_yet")} body="Once you join, assignments and announcements show up here." />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {rows.map((r) => (
            <li key={r.class_id} className="rounded-2xl border border-rule bg-card p-4">
              <p className="font-display text-lg font-bold text-ink">{r.classes?.name ?? "Class"}</p>
              <p className="mt-1 text-sm text-ink-muted">
                {[r.classes?.subject, r.classes?.grade_level].filter(Boolean).join(" · ") || "—"}
              </p>
              <Link to="/assignments" className="mt-3 inline-block text-sm font-semibold text-neon-pink">
                View assignments
              </Link>
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
